import React, { useState, useCallback } from 'react';

import { Box } from '@mui/material';

import ConfirmCancelModal from './ConfirmCancelModal';
import RequestDetailsModal from './RequestDetailsModal';
import RequestsList from './RequestsList';
import * as S from '../../LeadRequestOrchestration/styles';
import { useCancelLeadRequest } from '../hooks/useCancelLeadRequest';
import { useMyLeadRequests } from '../hooks/useMyLeadRequests';
import Button from 'components/Button';
import Text from 'components/Text';
import { useUser } from 'global/UserContext';
import { LeadRequest } from 'types/leadRequestsTypes';

interface MyLeadRequestListProps {
  onGoToForm: () => void;
}

export default function MyLeadRequestList({ onGoToForm }: MyLeadRequestListProps) {
  const { user } = useUser();
  const { requests, loading, error, refetch } = useMyLeadRequests(user?.id || '');
  const { cancelRequest } = useCancelLeadRequest();
  const [cancelingId, setCancelingId] = useState<string | null>(null);

  const [selectedRequest, setSelectedRequest] = useState<LeadRequest | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isConfirmCancelModalOpen, setIsConfirmCancelModalOpen] = useState(false);
  const [requestToCancel, setRequestToCancel] = useState<LeadRequest | null>(null);

  const handleViewDetails = useCallback((request: LeadRequest) => {
    setSelectedRequest(request);
    setIsDetailsModalOpen(true);
  }, []);

  const handleCloseDetailsModal = useCallback(() => {
    setIsDetailsModalOpen(false);
    setSelectedRequest(null);
  }, []);

  const handleOpenCancelConfirm = useCallback((request: LeadRequest) => {
    setRequestToCancel(request);
    setIsConfirmCancelModalOpen(true);
  }, []);

  const handleCloseCancelConfirm = useCallback(() => {
    setRequestToCancel(null);
    setIsConfirmCancelModalOpen(false);
  }, []);

  const handleConfirmCancel = useCallback(async () => {
    if (!requestToCancel) return;

    setCancelingId(requestToCancel.id);
    const success = await cancelRequest(requestToCancel.id);
    if (success) {
      refetch();
    }
    setCancelingId(null);
    handleCloseCancelConfirm();
  }, [cancelRequest, refetch, requestToCancel, handleCloseCancelConfirm]);

  return (
    <S.Container>
      {/* Header */}
      <S.Header>
        <S.Title>Minhas Solicitações de Lead</S.Title>
        <Text size="medium" color="textSecondary">
          Gerencie suas solicitações de leads
        </Text>
      </S.Header>

      {/* Action Bar */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Text size="medium" bold>
          {requests.length} {requests.length === 1 ? 'Solicitação' : 'Solicitações'} encontradas
        </Text>
        <Box>
          <Button variant="minimal" onClick={refetch} size="small" style={{ marginRight: '16px' }}>
            Atualizar
          </Button>
          <Button variant="primary" onClick={onGoToForm} size="medium">
            Criar nova solicitação
          </Button>
        </Box>
      </Box>

      {/* Requests List */}
      <RequestsList
        requests={requests}
        loading={loading}
        error={error}
        onViewDetails={handleViewDetails}
        onCancelRequest={handleOpenCancelConfirm}
        cancelingId={cancelingId}
      />

      {/* Request Details Modal */}
      <RequestDetailsModal
        request={selectedRequest}
        isOpen={isDetailsModalOpen}
        onClose={handleCloseDetailsModal}
      />

      {/* Confirm Cancellation Modal */}
      <ConfirmCancelModal
        isOpen={isConfirmCancelModalOpen}
        onClose={handleCloseCancelConfirm}
        onConfirm={handleConfirmCancel}
      />
    </S.Container>
  );
}
